import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { SealCheck, Camera, UploadSimple, ShieldCheck, LockKey, ArrowLeft } from "@phosphor-icons/react";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useLang } from "@/context/LangContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

const emptyForm = {
  legal_first_name: "",
  legal_last_name: "",
  date_of_birth: "",
  phone: "",
  address_line: "",
  postal_code: "",
  city: "",
  country: "",
};

export default function Verification() {
  const { t } = useLang();
  const { user, checkAuth } = useAuth();
  const navigate = useNavigate();
  const videoRef = useRef(null);
  const fileRef = useRef(null);
  const streamRef = useRef(null);

  const [status, setStatus] = useState(null);
  const [photoPreview, setPhotoPreview] = useState(null);
  const [selfiePreview, setSelfiePreview] = useState(null);
  const [camOn, setCamOn] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get("/verification/me").then(({ data }) => {
      setStatus(data);
      if (data.details) setForm({ ...emptyForm, ...data.details });
    }).catch(() => {});
    return () => streamRef.current?.getTracks().forEach((tr) => tr.stop());
  }, []);

  const upload = async (kind, blob, filename) => {
    const fd = new FormData();
    fd.append("kind", kind);
    fd.append("file", blob, filename);
    await api.post("/verification/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
    setStatus((s) => ({ ...(s || {}), [kind === "photo" ? "has_photo" : "has_selfie"]: true }));
    toast.success(t("ver_uploaded"));
  };

  const onPickPhoto = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setPhotoPreview(URL.createObjectURL(file));
    setBusy(true);
    try {
      await upload("photo", file, file.name);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const startCamera = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ video: { facingMode: "user" } });
      streamRef.current = stream;
      setCamOn(true);
      setTimeout(() => {
        if (videoRef.current) {
          videoRef.current.srcObject = stream;
          videoRef.current.play();
        }
      }, 50);
    } catch {
      toast.error(t("ver_camera_error"));
    }
  };

  const takeSelfie = async () => {
    const video = videoRef.current;
    if (!video) return;
    const canvas = document.createElement("canvas");
    canvas.width = video.videoWidth || 640;
    canvas.height = video.videoHeight || 480;
    canvas.getContext("2d").drawImage(video, 0, 0, canvas.width, canvas.height);
    const blob = await new Promise((res) => canvas.toBlob(res, "image/jpeg", 0.9));
    setSelfiePreview(URL.createObjectURL(blob));
    streamRef.current?.getTracks().forEach((tr) => tr.stop());
    setCamOn(false);
    setBusy(true);
    try {
      await upload("selfie", blob, "selfie.jpg");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/verification/submit", form);
      await checkAuth?.();
      toast.success(t("ver_done"));
      setStatus((s) => ({ ...(s || {}), verified: true, status: "verified" }));
      navigate("/dashboard");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const field = (key, type = "text", required = true) => (
    <div>
      <Label htmlFor={key}>{t(`ver_${key.replace("legal_", "").replace("_name", "").replace("address_line", "address").replace("date_of_birth", "dob").replace("postal_code", "postal")}`)}</Label>
      <Input
        id={key}
        type={type}
        required={required}
        data-testid={`ver-${key}`}
        value={form[key]}
        onChange={(ev) => setForm((f) => ({ ...f, [key]: ev.target.value }))}
        className="mt-1.5 rounded-xl border-ppc bg-ppc-bg"
      />
    </div>
  );

  return (
    <div className="mx-auto max-w-3xl px-5 pb-40 pt-8 md:pb-14 lg:px-8" data-testid="verification-page">
      <button onClick={() => navigate(-1)} data-testid="ver-back" className="flex items-center gap-1.5 text-sm text-ppc-muted hover:text-primary-green">
        <ArrowLeft size={15} /> {t("nav_dashboard")}
      </button>

      <div className="card-hero mt-5 p-6">
        <span className="glass-pill inline-flex items-center gap-1.5 text-[12px] text-ppc-text">
          <ShieldCheck size={14} weight="fill" color="#c6bff0" /> {t("ver_badge")}
        </span>
        <h1 className="mt-4 text-3xl leading-tight tracking-[-0.02em] text-ppc-text md:text-4xl">{t("ver_title")}</h1>
        <p className="mt-2.5 text-sm text-ppc-muted">{t("ver_sub")}</p>
        {(status?.verified || user?.verified) && (
          <p className="mt-4 flex items-center gap-2 text-sm text-primary-green" data-testid="ver-verified-note">
            <SealCheck size={16} weight="fill" color="#9184d9" /> {t("ver_done")} — {t("ver_done_d")}
          </p>
        )}
      </div>

      {/* Step 1 — photo */}
      <section className="card-list mt-4 p-6" data-testid="ver-photo-step">
        <div className="flex items-start justify-between gap-3">
          <div>
            <h2 className="text-base text-ppc-text md:text-lg">{t("ver_step_photo")}</h2>
            <p className="mt-1 text-[13px] text-ppc-muted">{t("ver_step_photo_d")}</p>
          </div>
          {status?.has_photo && <SealCheck size={20} weight="fill" color="#9184d9" className="shrink-0" />}
        </div>
        <div className="mt-4 flex items-center gap-4">
          <span className="thumb-glass grid h-20 w-20 shrink-0 place-items-center !rounded-2xl">
            {photoPreview ? (
              <img src={photoPreview} alt="" />
            ) : user?.verified ? (
              <img src={`${process.env.REACT_APP_BACKEND_URL}/api/providers/${user.id}/photo`} alt="" />
            ) : (
              <Camera size={22} color="rgba(233,233,237,0.5)" />
            )}
          </span>
          <input ref={fileRef} type="file" accept="image/jpeg,image/png,image/webp" onChange={onPickPhoto} className="hidden" data-testid="ver-photo-input" />
          <Button type="button" onClick={() => fileRef.current?.click()} disabled={busy} data-testid="ver-photo-btn" className="btn-secondary gap-1.5 rounded-xl">
            <UploadSimple size={16} /> {t("ver_choose_file")}
          </Button>
        </div>
      </section>

      {/* Step 2 — selfie */}
      <section className="card-list mt-4 p-6" data-testid="ver-selfie-step">
        <div className="flex items-start justify-between gap-3">
          <div>
            <h2 className="text-base text-ppc-text md:text-lg">{t("ver_step_selfie")}</h2>
            <p className="mt-1 text-[13px] text-ppc-muted">{t("ver_step_selfie_d")}</p>
          </div>
          {status?.has_selfie && <SealCheck size={20} weight="fill" color="#9184d9" className="shrink-0" />}
        </div>
        <div className="mt-4">
          {camOn ? (
            <div className="overflow-hidden rounded-2xl border border-ppc">
              <video ref={videoRef} playsInline muted className="h-64 w-full bg-black object-cover" data-testid="ver-video" />
            </div>
          ) : selfiePreview ? (
            <div className="overflow-hidden rounded-2xl border border-ppc">
              <img src={selfiePreview} alt="" className="h-64 w-full object-cover" />
            </div>
          ) : null}
          <div className="mt-4 flex flex-wrap gap-2.5">
            {!camOn ? (
              <Button type="button" onClick={startCamera} disabled={busy} data-testid="ver-camera-btn" className="btn-secondary gap-1.5 rounded-xl">
                <Camera size={16} /> {selfiePreview ? t("ver_retake") : t("ver_start_camera")}
              </Button>
            ) : (
              <Button type="button" onClick={takeSelfie} disabled={busy} data-testid="ver-take-btn" className="btn-primary gap-1.5 rounded-xl">
                <Camera size={16} /> {t("ver_take")}
              </Button>
            )}
          </div>
        </div>
      </section>

      {/* Step 3 — legal details */}
      <form onSubmit={submit} className="card-list mt-4 p-6" data-testid="ver-details-step">
        <h2 className="text-base text-ppc-text md:text-lg">{t("ver_step_details")}</h2>
        <p className="mt-1 text-[13px] text-ppc-muted">{t("ver_step_details_d")}</p>
        <div className="mt-5 grid gap-4 sm:grid-cols-2">
          {field("legal_first_name")}
          {field("legal_last_name")}
          {field("date_of_birth", "date", false)}
          {field("phone", "tel")}
          <div className="sm:col-span-2">{field("address_line")}</div>
          {field("postal_code")}
          {field("city")}
          <div className="sm:col-span-2">{field("country")}</div>
        </div>

        <p className="mt-5 flex items-start gap-2 rounded-2xl border border-ppc bg-ppc-bg p-4 text-[12.5px] leading-relaxed text-ppc-muted" data-testid="ver-privacy-note">
          <LockKey size={16} className="mt-0.5 shrink-0" color="#c6bff0" /> {t("ver_private_note")}
        </p>

        <Button type="submit" disabled={busy || !status?.has_photo || !status?.has_selfie} data-testid="ver-submit-btn" className="btn-primary mt-5 h-12 w-full rounded-xl">
          {t("ver_submit")}
        </Button>
      </form>

      <div className="card-focal mt-4 p-6">
        <h3 className="text-base text-ppc-text md:text-lg">{t("ver_why")}</h3>
        <p className="mt-2 text-[13px] leading-relaxed text-ppc-muted">{t("ver_why_d")}</p>
      </div>
    </div>
  );
}
